import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { SyringeIcon as Syringe, StethoscopeIcon as Stethoscope, ScalesIcon as Scales, CalendarCheckIcon as CalendarCheck, FirstAidIcon as FirstAid, SparkleIcon as Sparkle, ArrowRightIcon as ArrowRight } from "@phosphor-icons/react";
import { isNativeApp } from "@/lib/nativeBridge";

/**
 * Health Vault — owner-friendly aggregated read view of a dog's medical
 * data. Always renders the always-free signals (vaccination count, vet
 * visit count, latest manual weight, upcoming appointments). For Dog
 * Log subscribers we also surface a 90-day weight sparkline and recent
 * medication entries. For free-tier users we surface a polite upsell.
 *
 * Reads from `GET /api/me/health-vault/{dog_id}` (owner-gated).
 */
export default function HealthVault({ dogId }) {
  const [vault, setVault] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!dogId) return;
    let cancelled = false;
    api.get(`/me/health-vault/${dogId}`, { silent: true })
      .then((r) => { if (!cancelled) setVault(r.data); })
      .catch((e) => { if (!cancelled) setError(e); });
    return () => { cancelled = true; };
  }, [dogId]);

  if (error) return null; // graceful — fall through to legacy SubList
  if (!vault) return null;
  const { summary, vaccinations, vet_visits, other_records, upcoming_appointments,
    weight_points, medication_entries } = vault;

  return (
    <section className="space-y-4" data-testid="health-vault">
      {/* Summary tiles */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Tile icon={Syringe} label="Vaccinations" value={summary.vaccinations_on_file} tone="crimson" testid="vault-tile-vacc" />
        <Tile icon={Stethoscope} label="Vet visits" value={summary.vet_visits_on_file} tone="ink" testid="vault-tile-vet" />
        <Tile
          icon={Scales}
          label={summary.latest_weight_lbs ? "Latest weight" : "Weight"}
          value={summary.latest_weight_lbs ? `${summary.latest_weight_lbs} lb` : "—"}
          tone="gold"
          testid="vault-tile-weight"
        />
        <Tile
          icon={CalendarCheck}
          label="Upcoming appts"
          value={summary.upcoming_appointments}
          tone="ink"
          testid="vault-tile-appts"
        />
      </div>

      {/* Weight chart — Dog Log subscriber only. Free tier gets an upsell card (web only — hidden in native shells per App Review 3.1.1). */}
      {summary.dog_log_subscribed ? (
        <WeightChart points={weight_points} />
      ) : isNativeApp() ? null : (
        <div
          className="rounded-xl border-2 border-dashed border-[#E8D9A6] bg-[#FBF7EC] p-4 sm:p-5"
          data-testid="vault-doglog-upsell"
        >
          <div className="flex items-start gap-3">
            <span className="p-2 rounded-full bg-white">
              <Sparkle size={18} weight="duotone" className="text-gold-dark" />
            </span>
            <div className="flex-1 min-w-0">
              <h4 className="font-spectral text-base">Unlock weight charts &amp; medication tracking</h4>
              <p className="text-[12px] text-[#6B6B6B] mt-0.5">
                Dog Log adds daily weight logging, medication reminders, vet-visit history,
                and exportable reports for your vet — all from the same Health Vault you're
                already using.
              </p>
              <Link to="/dog-log" data-testid="vault-doglog-cta">
                <Button size="sm" className="mt-2 rounded-full bg-ink hover:bg-crimson text-white">
                  Try Dog Log <ArrowRight size={12} weight="bold" className="ml-1" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      )}

      {/* Upcoming vet appointments — surface raw rows so the owner knows what's next. */}
      {upcoming_appointments.length > 0 && (
        <div className="rounded-xl border border-[#E5E5E5] bg-white p-4" data-testid="vault-upcoming-appts">
          <h4 className="font-spectral text-base mb-2 flex items-center gap-2">
            <CalendarCheck size={16} weight="duotone" className="text-ink" />
            Upcoming care
          </h4>
          <ul className="space-y-1.5">
            {upcoming_appointments.slice(0, 5).map((a) => (
              <li key={a.id} className="text-sm flex items-center justify-between gap-3">
                <span className="font-medium text-ink truncate">{a.title || a.kind || "Care event"}</span>
                <span className="text-[12px] text-[#3F3F3F] whitespace-nowrap">
                  {new Date(a.starts_at).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })}
                </span>
              </li>
            ))}
          </ul>
          <Link to="/calendar" className="mt-2 inline-flex items-center gap-1 text-[12px] font-bold text-ink hover:text-crimson">
            Open calendar <ArrowRight size={12} weight="bold" />
          </Link>
        </div>
      )}

      {/* Three sub-lists. We RENDER ONLY summaries here — the existing
          SubList editor (which the parent renders) is the source of truth
          for adds/deletes. */}
      {vaccinations.length > 0 && (
        <VaultRecordList
          title="Vaccinations"
          icon={Syringe}
          items={vaccinations}
          testid="vault-vaccinations"
        />
      )}
      {vet_visits.length > 0 && (
        <VaultRecordList
          title="Vet visits & exams"
          icon={Stethoscope}
          items={vet_visits}
          testid="vault-vet-visits"
        />
      )}
      {other_records.length > 0 && (
        <VaultRecordList
          title="Other health notes"
          icon={FirstAid}
          items={other_records}
          testid="vault-other-records"
        />
      )}

      {/* Medications — subscriber only. */}
      {summary.dog_log_subscribed && medication_entries.length > 0 && (
        <div className="rounded-xl border border-[#E5E5E5] bg-white p-4" data-testid="vault-medications">
          <h4 className="font-spectral text-base mb-2 flex items-center gap-2">
            <FirstAid size={16} weight="duotone" className="text-crimson" />
            Recent medication logs
          </h4>
          <ul className="space-y-1.5">
            {medication_entries.slice().reverse().slice(0, 6).map((m, i) => (
              <li key={`${m.date}-${i}`} className="text-sm flex items-start justify-between gap-3">
                <span className="text-[#3F3F3F] truncate">{m.notes || "(no notes)"}</span>
                <span className="text-[12px] text-[#6B6B6B] whitespace-nowrap">
                  {new Date(m.date).toLocaleDateString(undefined, { month: "short", day: "numeric" })}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </section>
  );
}

function Tile({ icon: Icon, label, value, tone, testid }) {
  const toneCls = {
    crimson: "bg-[#FEE2E2] text-[#C1272D]",
    gold: "bg-[#FBF7EC] text-gold-dark border border-[#E8D9A6]",
    ink: "bg-ink/5 text-ink",
  }[tone] || "bg-[#FBF7EC] text-gold-dark";
  return (
    <div
      className="rounded-xl border border-[#E5E5E5] bg-white p-3 flex flex-col items-start gap-1"
      data-testid={testid}
    >
      <span className={`inline-flex items-center justify-center h-7 w-7 rounded-full ${toneCls}`}>
        <Icon size={14} weight="duotone" />
      </span>
      <span className="font-spectral text-2xl tabular-nums leading-none text-ink">{value}</span>
      <span className="text-[10px] font-bold uppercase tracking-[0.14em] text-[#6B6B6B]">{label}</span>
    </div>
  );
}

function VaultRecordList({ title, icon: Icon, items, testid }) {
  const fmt = (d) => {
    if (!d) return "";
    try { return new Date(d).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }); }
    catch { return d; }
  };
  return (
    <div className="rounded-xl border border-[#E5E5E5] bg-white p-4" data-testid={testid}>
      <h4 className="font-spectral text-base mb-2 flex items-center gap-2">
        <Icon size={16} weight="duotone" className="text-ink" />
        {title}
      </h4>
      <ul className="space-y-1.5">
        {items.slice(0, 6).map((h, i) => (
          <li key={h.id || `${h.date}-${i}`} className="text-sm flex items-start justify-between gap-3">
            <span className="text-[#3F3F3F] truncate"><strong className="text-ink">{h.type}</strong>{h.description ? ` — ${h.description}` : ""}{h.vet ? ` · ${h.vet}` : ""}</span>
            <span className="text-[12px] text-[#6B6B6B] whitespace-nowrap">{fmt(h.date)}</span>
          </li>
        ))}
      </ul>
      {items.length > 6 && (
        <p className="text-[11px] text-[#6B6B6B] mt-2">+ {items.length - 6} more — see full list below</p>
      )}
    </div>
  );
}

function WeightChart({ points }) {
  if (!points || points.length < 2) {
    return (
      <div className="rounded-xl border border-[#E5E5E5] bg-white p-4" data-testid="vault-weight-chart-empty">
        <h4 className="font-spectral text-base mb-1 flex items-center gap-2">
          <Scales size={16} weight="duotone" className="text-gold-dark" />
          Weight over time
        </h4>
        <p className="text-[12px] text-[#6B6B6B]">
          Log at least two weight entries in Dog Log to start seeing a trend.
        </p>
      </div>
    );
  }
  const W = 600, H = 140, P = 24;
  const xs = points.map((_, i) => P + (i / (points.length - 1)) * (W - 2 * P));
  const ys = points.map((p) => p.lbs);
  const minY = Math.min(...ys), maxY = Math.max(...ys);
  const span = Math.max(1, maxY - minY);
  const yPx = (y) => H - P - ((y - minY) / span) * (H - 2 * P);
  const d = xs.map((x, i) => `${i === 0 ? "M" : "L"}${x.toFixed(1)} ${yPx(ys[i]).toFixed(1)}`).join(" ");
  return (
    <div className="rounded-xl border border-[#E5E5E5] bg-white p-4" data-testid="vault-weight-chart">
      <h4 className="font-spectral text-base mb-2 flex items-center justify-between">
        <span className="flex items-center gap-2">
          <Scales size={16} weight="duotone" className="text-gold-dark" />
          Weight (last 90 days)
        </span>
        <span className="text-[12px] text-[#6B6B6B] font-tabular">
          {minY.toFixed(1)}–{maxY.toFixed(1)} lb · {points.length} entries
        </span>
      </h4>
      <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-32" preserveAspectRatio="none" role="img" aria-label="Weight trend chart">
        <path d={d} stroke="#C1272D" strokeWidth="2" fill="none" />
        {xs.map((x, i) => (
          <circle key={i} cx={x} cy={yPx(ys[i])} r="2.5" fill="#C1272D" />
        ))}
      </svg>
    </div>
  );
}
